import Model, { attr, belongsTo } from '@ember-data/model';
import { service } from '@ember/service';
import { isBlank, isEmpty, isPresent } from '@ember/utils';
import truncateFloat from 'eflex/util/truncate-float';
import { validator, buildValidations } from '@eflexsystems/ember-tracked-validations';
import { cached, tracked } from '@glimmer/tracking';
import { caption, arrayAttr } from 'eflex/decorators';
import { getOwner } from '@ember/application';
import { isString, isBoolean, isNumber } from 'ramda-adjunct';

@buildValidations({
  value: [
    validator('number', {
      allowString: false,
      integer: false,
      allowBlank: true,
      get disabled() {
        return this.model.type !== 'number';
      },
    }),

    validator('inline', {
      validate(value, options, jemProcessDatum) {
        if (jemProcessDatum.required && isBlank(value)) {
          return getOwner(jemProcessDatum).lookup('service:intl').t('errors.inputIsRequired');
        }

        const lowerNum = Number.parseFloat(jemProcessDatum.lowerLimit);
        const upperNum = Number.parseFloat(jemProcessDatum.upperLimit);
        const valueNum = Number.parseFloat(value);
        const valueOutOfRange = jemProcessDatum.type === 'number' && (valueNum < lowerNum || valueNum > upperNum);

        if (valueOutOfRange) {
          return getOwner(jemProcessDatum).lookup('service:intl').t('errors.invalidJemProcessDatum');
        }

        return true;
      },
    }),
  ],

})
class JemProcessDatum extends Model {
  @attr('string') variableDef;
  @attr('string') variableDefConfigId;
  @attr('number') lowerLimit;
  @attr('number') upperLimit;
  @attr('number', { defaultValue: 2 }) decimalLength;
  @attr value;
  @arrayAttr captions;
  @arrayAttr falseUnitCaptions;
  @arrayAttr unitCaptions;

  @caption name;
  @caption('falseUnitCaptions') falseUnit;
  @caption('unitCaptions') unit;

  @belongsTo('build-status-child', { inverse: 'processData', async: false }) buildStatusChild;

  @tracked wasFocused = false;

  @service store;
  @service intl;

  @cached
  get variableDefRecord() {
    if (!this.variableDef) {
      return null;
    }

    return this.store.peekRecord('variable-def', this.variableDef);
  }

  @cached
  get type() {
    if (this.variableDefRecord?.type) {
      return this.variableDefRecord.type;
    }

    const value = this.value;

    switch (true) {
      case isNumber(value): {
        return 'number';
      }
      case isBoolean(value):
      case (value === 'true' || value === 'false'): {
        return 'boolean';
      }
      case isString(value): {
        return 'string';
      }
      default: {
        return 'string';
      }
    }
  }

  get unitName() {
    if (isEmpty(this.unitCaptions)) {
      return this.intl.t('yes');
    } else {
      return this.unit;
    }
  }

  get falseUnitName() {
    if (isEmpty(this.falseUnitCaptions)) {
      return this.intl.t('no');
    } else {
      return this.falseUnit;
    }
  }

  get required() {
    return this.variableDefConfig?.required;
  }

  get timestamp() {
    return this.buildStatusChild?.timestamp;
  }

  @cached
  get variableDefConfig() {
    if (this.variableDefConfigId != null) {
      return this.store.peekRecord('variable-def-config', this.variableDefConfigId);
    } else {
      return null;
    }
  }

  get displayName() {
    const taskName = this.buildStatusChild.taskName;
    if (isPresent(taskName)) {
      return `${taskName} - ${this.name}`;
    } else {
      return this.name;
    }
  }

  get displayValue() {
    if (this.type === 'boolean') {
      if (this.value === true || this.value === 'true') {
        return this.unitName;
      } else {
        return this.falseUnitName;
      }
    }

    let value;

    if (this.type === 'float' || this.type === 'number' && this.value?.toFixed != null) {
      value = truncateFloat(this.value, this.decimalLength);
    } else {
      value = this.value;
    }

    if (isPresent(value) && isPresent(this.unitCaptions)) {
      value = `${value} ${this.unitName}`;
    }

    return value;
  }
}

export default JemProcessDatum;
